import { format, parse } from 'date-fns';
import { formatInTimeZone, zonedTimeToUtc } from 'date-fns-tz';
import { enAU } from 'date-fns/locale';
import { useTranslation } from 'react-i18next';
import { formatJobDateFromParts } from '~/components/job/formatDate';
import { formatDate } from '~/helpers/formatDate';

// Constant for unscheduled jobs marker (shared with JobList and others)
export const UNSCHEDULED_MARKER = 'UNSCHEDULED';

type DateDisplayProps = {
  // Date used for grouping; usually a property-local 'yyyy-MM-dd' string,
  // sometimes an ISO timestamp, or a preformatted label for special cases.
  date: string | null | undefined;
  // Optional job object for property-local date formatting
  job?: {
    timeZone?: string | null;
    scheduledStartDate?: string | null;
    scheduledStartTime?: string | null;
    scheduledEndDate?: string | null;
    scheduledEndTime?: string | null;
  } | null;
};

export function DateDisplay({ date, job }: DateDisplayProps) {
  const { t } = useTranslation();

  // Use data-date attribute to allow scrolling to today's anchor
  if (!date || typeof date !== 'string' || !date.trim()) return null;

  // Handle UNSCHEDULED marker for completed jobs without completedAt
  if (date === UNSCHEDULED_MARKER) {
    return (
      <div
        data-date='unscheduled'
        className='sticky top-0 z-10 mb-1 flex justify-center pt-2'
      >
        <span className='inline-block rounded-full border border-outline-bright bg-white px-4 py-1.5 text-xs font-medium text-secondary shadow-sm'>
          {t('unscheduled')}
        </span>
      </div>
    );
  }

  // Check if date is YYYY-MM-DD format (property-local date string)
  const isYYYYMMDD = /^\d{4}-\d{2}-\d{2}$/.test(date.trim());

  let formattedDate: string;
  let dataDate: string;

  if (isYYYYMMDD && job?.timeZone) {
    // Property-local YYYY-MM-DD string - use helper to format with job timezone
    // Extract timeZone with type guard
    const timeZone = job.timeZone;
    if (typeof timeZone !== 'string') {
      // Fallback if timeZone is null/undefined
      return null;
    }

    // Check if this date matches job's scheduledStartDate or scheduledEndDate
    const field =
      job.scheduledStartDate === date
        ? 'scheduledStart'
        : job.scheduledEndDate === date
        ? 'scheduledEnd'
        : null;

    if (field) {
      // Use helper which handles timezone internally
      formattedDate =
        formatJobDateFromParts({ ...job, timeZone }, field, 'PP') || '';
      dataDate = date.trim();
    } else {
      // Date doesn't match job fields, format directly with timezone
      try {
        const isoLocal = `${date.trim()}T00:00:00`;
        const utcDate = zonedTimeToUtc(isoLocal, timeZone);
        if (isNaN(utcDate.getTime())) {
          return null;
        }
        formattedDate = formatInTimeZone(utcDate, timeZone, 'PP', {
          locale: enAU,
        });
        dataDate = date.trim();
      } catch {
        return null;
      }
    }
  } else if (isYYYYMMDD) {
    // YYYY-MM-DD without job/timezone - parse as local (fallback)
    try {
      const parsed = parse(date.trim(), 'yyyy-MM-dd', new Date());
      if (isNaN(parsed.getTime())) {
        return null;
      }
      formattedDate = format(parsed, 'PP', { locale: enAU });
      dataDate = date.trim();
    } catch {
      return null;
    }
  } else {
    // Non-YYYY-MM-DD strings:
    // First check if it's an ISO timestamp (e.g., 2025-11-20T10:30:00Z or 2025-11-20T10:30:00.000Z)
    // ISO timestamps contain letters (T, Z) but should be formatted, not displayed raw.
    // Pattern matches: YYYY-MM-DDTHH:MM:SS with optional milliseconds and optional Z or timezone offset
    const isISOTimestamp =
      /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d{3})?(Z|[+-]\d{2}:\d{2})?$/.test(
        date.trim()
      );

    if (isISOTimestamp) {
      // ISO timestamp - format it via our helper
      formattedDate = formatDate(date, 'PP');
      dataDate = formatDate(date, 'yyyy-MM-dd');
    } else {
      // Not an ISO timestamp - check if it's a preformatted label
      // If the string already looks like a human-readable label (contains letters),
      // display it as-is (used by some callers that pre-format dates).
      const looksPreformatted = /[a-zA-Z]/.test(date);

      if (looksPreformatted) {
        formattedDate = date.trim();
        dataDate = date.trim();
      } else {
        // Numeric string without letters - try to format as ISO date
        formattedDate = formatDate(date, 'PP');
        dataDate = formatDate(date, 'yyyy-MM-dd');
      }
    }
  }

  // If date formatting fails, don't render anything
  if (!formattedDate || !dataDate) return null;

  return (
    <div
      data-date={dataDate}
      className='sticky top-0 z-10 mb-3 mt-2 flex justify-center pt-2'
    >
      <span className='inline-block rounded-full border border-outline-bright bg-white px-4 py-1.5 text-xs font-medium text-secondary shadow-sm'>
        {formattedDate}
      </span>
    </div>
  );
}
